import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { LISTING_SCHEMA, buildRenderedSections } from "../../lib/listingSchema";
import { useContent } from "../../lib/content";
import { Check, ChevronLeft, ChevronRight, Save, CheckCircle2, Sparkles, Info, Trash2 } from "lucide-react";

// ---------- Field renderers ----------
function TextField({ field, value, onChange }) {
  const inputType = field.type === "number" ? "number" : field.type === "tel" ? "tel" : field.type === "date" ? "date" : "text";
  return (
    <div style={{ position: "relative" }}>
      {field.prefix && (
        <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }}>{field.prefix}</span>
      )}
      <input
        data-testid={`li-${field.name}`}
        className="input-field"
        type={inputType}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={field.prefix ? { paddingLeft: 28 } : undefined}
        placeholder={field.placeholder}
      />
    </div>
  );
}

function TextareaField({ field, value, onChange }) {
  return (
    <textarea data-testid={`li-${field.name}`} className="input-field" rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
  );
}

function SelectField({ field, value, onChange }) {
  return (
    <select data-testid={`li-${field.name}`} className="input-field" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      {field.options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
    </select>
  );
}

function RadioField({ field, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {field.options.map((o) => {
        const on = value === o;
        return (
          <button
            key={o}
            type="button"
            data-testid={`li-${field.name}-${o.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onChange(on ? "" : o)}
            style={{
              padding: "8px 16px",
              borderRadius: 9999,
              border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
              background: on ? "var(--primary)" : "#fff",
              color: on ? "#fff" : "var(--text)",
              cursor: "pointer",
              fontSize: 14,
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >{o}</button>
        );
      })}
    </div>
  );
}

function ToggleField({ field, value, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} data-testid={`li-${field.name}-label`}>
      <div style={{
        width: 44, height: 24, borderRadius: 9999, padding: 2,
        background: value ? "var(--primary)" : "var(--border)",
        transition: "background 0.2s",
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: "50%", background: "#fff",
          transform: value ? "translateX(20px)" : "translateX(0)",
          transition: "transform 0.2s",
        }} />
      </div>
      <input data-testid={`li-${field.name}`} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} style={{ display: "none" }} />
      <span style={{ fontSize: 14, color: "var(--text-muted)" }}>{value ? "Yes" : "No"}</span>
    </label>
  );
}

function MultiField({ field, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    onChange(selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt]);
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
      {field.options.map((opt) => {
        const on = selected.includes(opt);
        return (
          <label
            key={opt}
            data-testid={`li-${field.name}-${opt.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px",
              border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
              borderRadius: 10,
              background: on ? "var(--surface-alt)" : "#fff",
              cursor: "pointer",
              fontSize: 13,
              transition: "all 0.15s",
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
              border: `1.5px solid ${on ? "var(--primary)" : "var(--text-faint)"}`,
              background: on ? "var(--primary)" : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{on && <Check size={11} color="#fff" strokeWidth={3} />}</div>
            <span>{opt}</span>
            <input type="checkbox" checked={on} onChange={() => toggle(opt)} style={{ display: "none" }} />
          </label>
        );
      })}
    </div>
  );
}

function GroupHeader({ group, popoverOpen, setPopoverOpen, span }) {
  const open = popoverOpen === group.id;
  const hasPopover = !!(group.detail || group.hint);
  const toggle = () => {
    if (!hasPopover) return;
    setPopoverOpen(open ? null : group.id);
  };
  return (
    <th
      colSpan={span}
      style={{
        padding: "10px 8px 8px",
        textAlign: "center",
        position: "relative",
        borderBottom: span > 1 ? "1px solid var(--border)" : undefined,
        background: group.primary ? "#E8F0EA" : "var(--surface-alt)",
      }}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid={`rooms-header-${group.id}`}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: hasPopover ? "pointer" : "default",
          color: group.primary ? "var(--primary)" : "var(--text-muted)",
          fontWeight: group.primary ? 700 : 600,
          fontSize: 11,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          fontFamily: "inherit",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        {group.label}
        {hasPopover && <Info size={11} aria-hidden="true" />}
      </button>
      {open && (
        <div
          role="dialog"
          data-testid={`rooms-popover-${group.id}`}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            width: 280,
            background: "#fff",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
            padding: 18,
            textAlign: "left",
            textTransform: "none",
            letterSpacing: 0,
            fontWeight: 400,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>{group.label}</div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPopoverOpen(null); }}
              aria-label="Close"
              data-testid={`rooms-popover-close-${group.id}`}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--text-muted)", fontSize: 18, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
          {group.hint && <div style={{ color: "var(--text-muted)" }}>{group.hint}</div>}
          {group.detail && (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {group.detail.map(([abbrev, meaning]) => (
                <li key={abbrev}>
                  <b style={{ fontWeight: 700 }}>{abbrev}</b>
                  <span style={{ color: "var(--text-muted)" }}> — {meaning}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </th>
  );
}

function GridField({ field, value, onChange }) {
  const v = value || {};
  const [popoverOpen, setPopoverOpen] = useState(null);
  const set = (rowId, colId, n) => {
    const next = { ...v, [rowId]: { ...(v[rowId] || {}), [colId]: n } };
    if (!n) delete next[rowId][colId];
    onChange(next);
  };
  // Compute column totals (used for Sq Ft summary row)
  const colTotal = (colId) => {
    let total = 0;
    for (const r of field.rows) {
      const cellValue = Number(v[r.id]?.[colId]) || 0;
      total += cellValue;
    }
    return total;
  };
  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e) => {
      if (!e.target.closest(`[data-testid^="rooms-popover-"]`) && !e.target.closest(`[data-testid^="rooms-header-"]`)) {
        setPopoverOpen(null);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [popoverOpen]);

  // Use colGroups when provided, otherwise fall back to a flat single-row header (legacy behavior).
  const groups = field.colGroups;
  const flatCols = field.cols;
  const groupedHeader = !!groups;

  return (
    <div>
      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.5 }}>
        For each level you have, enter the square footage and how many of each room. Click a column header (with the ⓘ icon) to see what each abbreviation means. Totals calculate automatically.
      </p>
      <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 12 }}>
      <table style={{ borderCollapse: "collapse", minWidth: "100%" }} data-testid={`li-${field.name}-grid`}>
        <thead>
          {groupedHeader ? (
            <>
              <tr style={{ background: "var(--surface-alt)" }}>
                <th rowSpan={2} style={{ padding: "10px 12px", textAlign: "left", position: "sticky", left: 0, background: "var(--surface-alt)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", zIndex: 2, verticalAlign: "bottom" }}>Level</th>
                {groups.map((g) => (
                  <GroupHeader
                    key={g.id}
                    group={g}
                    span={g.cols.length}
                    popoverOpen={popoverOpen}
                    setPopoverOpen={setPopoverOpen}
                  />
                ))}
              </tr>
              <tr style={{ background: "var(--surface-alt)" }}>
                {groups.flatMap((g) =>
                  g.cols.length > 1
                    ? g.cols.map((c) => (
                        <th key={c.id} style={{ padding: "0 8px 8px", textAlign: "center", fontSize: 11, letterSpacing: "0.05em", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", background: g.primary ? "#E8F0EA" : "var(--surface-alt)" }}>
                          {c.label}
                        </th>
                      ))
                    : [<th key={g.cols[0].id} style={{ background: g.primary ? "#E8F0EA" : "var(--surface-alt)" }} aria-hidden="true" />]
                )}
              </tr>
            </>
          ) : (
            <tr style={{ background: "var(--surface-alt)" }}>
              <th style={{ padding: "10px 12px", textAlign: "left", position: "sticky", left: 0, background: "var(--surface-alt)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", zIndex: 1 }}>Level</th>
              {flatCols.map((c) => (
                <th key={c.id} style={{ padding: "10px 8px", textAlign: "center", fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>{c.label}</th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {field.rows.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500, position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>{r.label}</td>
              {flatCols.map((c) => (
                <td key={c.id} style={{ padding: "4px" }}>
                  <input
                    type="number"
                    min="0"
                    data-testid={`li-rooms-${r.id}-${c.id}`}
                    value={(v[r.id]?.[c.id]) ?? ""}
                    onChange={(e) => set(r.id, c.id, e.target.value ? Number(e.target.value) : 0)}
                    style={{ width: c.width || 60, padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, textAlign: "center", fontFamily: "inherit" }}
                  />
                </td>
              ))}
            </tr>
          ))}
          <tr style={{ borderTop: "2px solid var(--border)", background: "var(--surface-alt)" }} data-testid={`li-${field.name}-totals`}>
            <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", position: "sticky", left: 0, background: "var(--surface-alt)", zIndex: 1 }}>Totals</td>
            {flatCols.map((c) => {
              const total = colTotal(c.id);
              return (
                <td key={c.id} style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, fontWeight: 600 }} data-testid={`li-${field.name}-total-${c.id}`}>
                  {total > 0 ? total.toLocaleString() : "—"}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  switch (field.type) {
    case "textarea": return <TextareaField field={field} value={value} onChange={onChange} />;
    case "select": return <SelectField field={field} value={value} onChange={onChange} />;
    case "radio": return <RadioField field={field} value={value} onChange={onChange} />;
    case "toggle": return <ToggleField field={field} value={value} onChange={onChange} />;
    case "multi": return <MultiField field={field} value={value} onChange={onChange} />;
    case "grid": return <GridField field={field} value={value} onChange={onChange} />;
    default: return <TextField field={field} value={value} onChange={onChange} />;
  }
}

// ---------- Wizard ----------
export default function ListingInputForm({
  onSubmitted,
  draftEndpoint = "/forms/draft/listing_input",
  adminMode = false,
  sellerName = "",
  onAdminSaved,
  reloadKey = 0,
}) {
  const steps = LISTING_SCHEMA.steps;
  const { content } = useContent();
  const listingDisclaimer = content?.forms?.listing_disclaimer;
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({});
  const [prefilledFields, setPrefilledFields] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const loaded = useRef(false);
  // Stable ref for the parent callback so the auto-save effect's deps don't churn
  // every parent render (which would re-queue 1.2s timers that can race with reload).
  const onAdminSavedRef = useRef(onAdminSaved);
  useEffect(() => { onAdminSavedRef.current = onAdminSaved; }, [onAdminSaved]);

  // Load draft on mount AND whenever the parent bumps reloadKey (e.g. after PDF auto-fill).
  useEffect(() => {
    (async () => {
      try {
        // Pause auto-save while we re-hydrate so we don't write empty values back.
        loaded.current = false;
        // Cancel any pending auto-save so a stale 1.2s timer can't clobber the freshly loaded draft.
        if (saveTimer.current) {
          clearTimeout(saveTimer.current);
          saveTimer.current = null;
        }
        const { data } = await api.get(draftEndpoint);
        if (data?.data) setValues(data.data);
        else setValues({});
        if (Array.isArray(data?.prefilled_fields)) {
          setPrefilledFields(new Set(data.prefilled_fields));
        } else {
          setPrefilledFields(new Set());
        }
      } catch {}
      loaded.current = true;
    })();
  }, [draftEndpoint, reloadKey]);

  // Auto-save draft (debounced). onAdminSavedRef is intentionally NOT in deps so this effect
  // only re-runs when values/draftEndpoint actually change (not on every parent render).
  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(draftEndpoint, { data: values });
        setSavedAt(new Date());
        if (adminMode && onAdminSavedRef.current) onAdminSavedRef.current();
      } catch {}
    }, 1200);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [values, draftEndpoint, adminMode]);

  const setField = useCallback((name, v) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    // Once the seller edits a field, it's no longer "pre-filled by agent" — drop the badge locally.
    if (!adminMode) {
      setPrefilledFields((prev) => {
        if (!prev.has(name)) return prev;
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  }, [adminMode]);

  const currentStep = steps[stepIdx];

  // Validation for required fields runs only at final submit (allMissing below).
  // Sellers can freely navigate between steps and come back to fill required fields.

  const allMissing = useMemo(() => {
    const missing = [];
    steps.forEach((s) => s.sections.forEach((sec) => sec.fields.forEach((f) => {
      if (f.required) {
        const v = values[f.name];
        if (v === undefined || v === null || v === "") missing.push(f.label);
      }
    })));
    return missing;
  }, [steps, values]);

  const submitAll = async () => {
    if (allMissing.length) { setErr(`Please fill required fields: ${allMissing.join(", ")}`); return; }
    setErr(""); setBusy(true);
    try {
      const rendered_sections = buildRenderedSections(values);
      const { data } = await api.post("/forms/submit", {
        form_type: "listing_input",
        data: { ...values, rendered_sections },
        send_email: true,
      });
      // Keep the draft populated post-submit so the seller can come back, review, and tweak;
      // and so the admin still sees the data on their end. (Previously we deleted the draft here.)
      setSavedAt(new Date());
      onSubmitted(data);
    } catch (e) {
      // Surface the real failure mode so we can act on it instead of a generic toast.
      console.error("Listing form submit failed:", e, e.response);
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      let msg;
      if (detail) msg = formatApiError(detail);
      else if (status) msg = `Submit failed (HTTP ${status}). ${e.message || ""}`.trim();
      else msg = e.message || "Submit failed. Check your connection and try again.";
      setErr(msg);
    } finally { setBusy(false); }
  };

  const adminSaveNow = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(draftEndpoint, { data: values });
      setSavedAt(new Date());
      if (onAdminSaved) onAdminSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear every field on the listing form? This can't be undone.")) return;
    setBusy(true);
    setErr("");
    try {
      // Pause auto-save so the empty state actually persists.
      loaded.current = false;
      setValues({});
      setPrefilledFields(new Set());
      await api.put(draftEndpoint, { data: {} });
      setSavedAt(new Date());
      loaded.current = true;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      loaded.current = true;
    } finally { setBusy(false); }
  };

  const go = (dir) => {
    // Allow free navigation between steps — sellers can skip ahead and come back.
    // Required-field validation still runs at final submit (see submitAll).
    setErr("");
    const ni = Math.max(0, Math.min(steps.length - 1, stepIdx + dir));
    setStepIdx(ni);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pct = Math.round(((stepIdx + 1) / steps.length) * 100);

  return (
    <div className="wizard-grid">
      {/* Sidebar */}
      <aside className="card wizard-sidebar" style={{ padding: 20 }} data-testid="listing-wizard-sidebar">
        <div className="overline">Listing form</div>
        <div style={{ marginTop: 8, marginBottom: 12, fontSize: 13, color: "var(--text-muted)" }}>
          Step {stepIdx + 1} of {steps.length} · {pct}%
        </div>
        <div style={{ height: 6, background: "var(--border)", borderRadius: 9999, overflow: "hidden", marginBottom: 16 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--primary)", transition: "width 0.3s" }} />
        </div>
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {steps.map((s, i) => {
            const active = i === stepIdx;
            const done = i < stepIdx;
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`wizard-step-${s.id}`}
                onClick={() => { setErr(""); setStepIdx(i); }}
                style={{
                  textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "none",
                  background: active ? "var(--surface-alt)" : "transparent",
                  color: active ? "var(--primary)" : "var(--text)",
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer", fontFamily: "inherit", fontSize: 13,
                  display: "flex", alignItems: "center", gap: 8,
                }}
              >
                <span style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: done ? "var(--primary)" : active ? "#fff" : "var(--border)",
                  color: done ? "#fff" : "var(--text-muted)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 600,
                  border: active ? "2px solid var(--primary)" : "none",
                  flexShrink: 0,
                }}>{done ? <Check size={11} strokeWidth={3} /> : i + 1}</span>
                {s.title}
              </button>
            );
          })}
        </nav>
        <div className="gradient-divider" style={{ margin: "16px 0" }} />
        <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
          <Save size={12} /> {savedAt ? `Draft saved ${savedAt.toLocaleTimeString()}` : "Draft saves automatically"}
        </div>
      </aside>

      {/* Content */}
      <div>
        {!adminMode && listingDisclaimer?.enabled !== false && (listingDisclaimer?.title || listingDisclaimer?.body) && (
          <div
            data-testid="seller-listing-disclaimer"
            style={{
              marginBottom: 16,
              padding: "14px 18px",
              borderRadius: 12,
              background: "#F0EDE3",
              border: "1px solid var(--border)",
              fontSize: 13.5,
              lineHeight: 1.55,
              color: "var(--text)",
            }}
          >
            {listingDisclaimer?.title && (
              <b style={{ display: "block", marginBottom: 4 }}>{listingDisclaimer.title}</b>
            )}
            {listingDisclaimer?.body && (
              <span style={{ whiteSpace: "pre-wrap" }}>{listingDisclaimer.body}</span>
            )}
          </div>
        )}
        {adminMode && (
          <div
            data-testid="admin-prefill-banner"
            style={{
              marginBottom: 16,
              padding: "12px 16px",
              borderRadius: 12,
              background: "#F0EDE3",
              border: "1px solid var(--border)",
              fontSize: 14,
              color: "var(--text)",
            }}
          >
            <b>Pre-filling for {sellerName || "this seller"}.</b> Anything you enter here saves
            automatically and shows up in their Listing Input form. They can review and edit
            before submitting.
          </div>
        )}
        <div style={{ marginBottom: 24 }}>
          <span className="overline">{currentStep.title}</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 500, margin: "6px 0 4px", letterSpacing: "-0.01em" }} data-testid="wizard-current-title">
            {currentStep.title}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
            {adminMode
              ? "Fill in any sections you have info for. Your changes auto-save and pre-fill the seller's form."
              : "Fill in what applies. You can save now and finish later — your draft is auto-saved."}
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {currentStep.sections.map((sec) => (
            <div key={sec.id} className="card" style={{ padding: 28 }} data-testid={`wizard-section-${sec.id}`}>
              <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "0 0 16px", letterSpacing: "-0.01em" }}>{sec.title}</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
                {sec.fields.map((f) => {
                  const full = ["textarea", "multi", "grid"].includes(f.type);
                  const showPrefilledBadge = !adminMode && prefilledFields.has(f.name);
                  return (
                    <div key={f.name} style={{ gridColumn: full ? "1 / -1" : "auto" }}>
                      <label style={{ display: "flex", gap: 6, fontSize: 13, fontWeight: 500, marginBottom: 6, alignItems: "baseline", flexWrap: "wrap" }}>
                        <span>{f.label}</span>
                        {f.required && <span style={{ color: "var(--error)" }}>*</span>}
                        {showPrefilledBadge && (
                          <span
                            data-testid={`prefilled-badge-${f.name}`}
                            title="Your agent pre-filled this field. Edit it any time."
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontSize: 10,
                              fontWeight: 500,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              padding: "2px 8px",
                              borderRadius: 9999,
                              background: "var(--surface-alt)",
                              color: "var(--primary)",
                              border: "1px solid var(--border)",
                            }}
                          >
                            <Sparkles size={10} /> Pre-filled by your agent
                          </span>
                        )}
                      </label>
                      <FieldRenderer field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />
                      {f.hint && <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>{f.hint}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {err && <div data-testid="listing-wizard-error" style={{ marginTop: 16, padding: "12px 16px", borderRadius: 12, background: "#F7E2E2", color: "var(--error)", fontSize: 14 }}>{err}</div>}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button data-testid="wizard-prev-btn" type="button" className="btn-secondary" onClick={() => go(-1)} disabled={stepIdx === 0}>
              <ChevronLeft size={16} /> Previous
            </button>
            <button data-testid="wizard-clear-btn" type="button" className="btn-ghost" onClick={clearAll} disabled={busy} style={{ color: "var(--error)" }}>
              <Trash2 size={14} /> Clear all
            </button>
          </div>

          {stepIdx < steps.length - 1 ? (
            <button data-testid="wizard-next-btn" type="button" className="btn-primary" onClick={() => go(1)}>
              Next <ChevronRight size={16} />
            </button>
          ) : adminMode ? (
            <button data-testid="wizard-admin-save-btn" type="button" className="btn-primary" onClick={adminSaveNow} disabled={busy}>
              {busy ? "Saving…" : (<><Save size={16} /> Save now</>)}
            </button>
          ) : (
            <button data-testid="wizard-submit-btn" type="button" className="btn-primary" onClick={submitAll} disabled={busy}>
              {busy ? "Submitting…" : (<><CheckCircle2 size={16} /> Submit & Email PDF</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
